import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createMealPlanApi,
  fetchMacroSplitTemplatesApi,
  fetchMealMealPlanDetailsApi,
  fetchMealRestrictionsApi,
  fetchMealTagsApi,
  fetchRecipesBasedMealTypeReplaceRecipeModalApi,
  fetchRecipesFilterApi,
} from "../../api/clientMealsTabsApi";
import * as Endpoints from "../../entities/Endpoints";

export const fetchMealRestrictions = createAsyncThunk(
  `${Endpoints.GET_MEAL_RESTRICTIONS}`,
  async (_, { rejectWithValue }) => {
    try {
      return await fetchMealRestrictionsApi();
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch meal restrictions"
      );
    }
  }
);

export const fetchRecipesBasedMealType = createAsyncThunk(
  `${Endpoints.GET_CUSTOMIZED_RECIPES}`,
  async ({ requestPayload, mealTime }, { rejectWithValue }) => {
    try {
      const data = await fetchRecipesFilterApi({
        requestPayload,
        mealTime,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchRecipesBasedMealTypeReplaceRecipeModalThunk =
  createAsyncThunk(
    `${Endpoints.GET_STANDARD_RECIPES}`,
    async ({ requestPayload, mealTime }, { rejectWithValue }) => {
      try {
        const data = await fetchRecipesBasedMealTypeReplaceRecipeModalApi({
          requestPayload,
          mealTime,
        });
        return {
          data,
          requestedPage: requestPayload.pagination.pageNumber,
          mealTime,
        };
      } catch (error) {
        return rejectWithValue(error.message);
      }
    }
  );

export const fetchMealTags = createAsyncThunk(
  Endpoints.GET_FILTERED_RECIPE_WITH_MEAL_TIME,
  async ({ mealTime }, { rejectWithValue }) => {
    try {
      return await fetchMealTagsApi({ mealTime });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchMealPlanDetails = createAsyncThunk(
  Endpoints.GET_MEAL_PLAN_DETAILS,
  async ({ clientId }, { rejectWithValue }) => {
    try {
      return await fetchMealMealPlanDetailsApi({ clientId });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createMealPlanThunk = createAsyncThunk(
  Endpoints.UPSERT_MEAL_PLAN,
  async (requestPayload, thunkAPI) => {
    try {
      const data = await createMealPlanApi(requestPayload);
      if (requestPayload?.clientId) {
        thunkAPI.dispatch(
          fetchMealPlanDetails({ clientId: requestPayload.clientId })
        );
      }

      return data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const fetchMacroSplitTemplatesThunk = createAsyncThunk(
  Endpoints.GET_MACRO_SPLIT_TEMPLATES,
  async (_, { rejectWithValue }) => {
    try {
      return await fetchMacroSplitTemplatesApi();
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch macro split templates"
      );
    }
  }
);

const mealTagsSlice = createSlice({
  name: "mealTags",
  initialState: {
    mealRestrictions: [],
    loading: false,
    error: null,
    filteredRecipes: [],
    filterIsLoading: false,
    filterError: null,
    filterRecipesSuccessMessage: "",
    filterRecipesFailedMessage: "",
    createMealPlanLoading: false,
    createMealPlanSuccessMessage: "",
    createMealPlanErrorMessage: "",
    mealPlanDetails: null,
    mealPlanDetailsLoading: false,
    mealPlanDetailsError: null,
    mealPlanDetailsSuccessMessage: "",
    mealPlanDetailsFailedMessage: "",

    filterReplaceModal: {},
    filterReplaceModalIsLoading: false,
    filterReplaceModalError: null,
    filterReplaceModalSuccessMessage: "",
    filterReplaceModalFailedMessage: "",

    // For macro split templates
    macroSplitTemplates: [],
    macroSplitTemplatesLoading: false,
    macroSplitTemplatesError: null,

    // Pagination state for replace modal
    pageNumber: 1,
    pageSize: 20,
    hasMore: true,
  },
  reducers: {
    resetFilteredRecipes: (state) => {
      state.filteredRecipes = [];
    },
    resetMealPlanDetails: (state) => {
      state.mealPlanDetails = null;
      state.filteredRecipes = [];
    },
    resetState: (state) => {
      state.createMealPlanSuccessMessage = "";
      state.createMealPlanErrorMessage = "";
    },
    resetMacroSplitTemplates: (state) => {
      state.macroSplitTemplates = [];
    },
    resetModalPagination: (state) => {
      state.pageNumber = 1;
      state.hasMore = true;
      state.filterReplaceModal = {};
    },
    setModalPageNumber: (state, action) => {
      state.pageNumber = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Meal Restrictions
      .addCase(fetchMealRestrictions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMealRestrictions.fulfilled, (state, action) => {
        state.mealRestrictions = action.payload;
        state.loading = false;
      })
      .addCase(fetchMealRestrictions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Filter recipes
      .addCase(fetchRecipesBasedMealType.pending, (state) => {
        state.filterIsLoading = true;
        state.filterError = null;
        state.filterRecipesSuccessMessage = "";
        state.filterRecipesFailedMessage = "";
      })
      .addCase(fetchRecipesBasedMealType.fulfilled, (state, action) => {
        state.filterIsLoading = false;
        state.filteredRecipes = {
          ...state.filteredRecipes,
          ...action.payload,
        };
      })
      .addCase(fetchRecipesBasedMealType.rejected, (state, action) => {
        state.filterIsLoading = false;
        state.filterError = action.payload;
        state.filterRecipesFailedMessage = action.payload;
        state.filterRecipesFailedMessage = "No recipe found ";
      })

      // Filter recipes replace recipe modal with pagination
      .addCase(
        fetchRecipesBasedMealTypeReplaceRecipeModalThunk.pending,
        (state) => {
          state.filterReplaceModalIsLoading = true;
          state.filterReplaceModalError = null;
          state.filterReplaceModalSuccessMessage = "";
          state.filterReplaceModalFailedMessage = "";
        }
      )
      .addCase(
        fetchRecipesBasedMealTypeReplaceRecipeModalThunk.fulfilled,
        (state, action) => {
          state.filterReplaceModalIsLoading = false;
          const { data: newData, requestedPage, mealTime } = action.payload;
          const recipes = newData?.[mealTime] || [];

          // Update page number
          state.pageNumber = requestedPage;

          // Always replace recipes for per-page display (not append)
          state.filterReplaceModal = {
            ...state.filterReplaceModal,
            [mealTime]: recipes,
          };

          // Determine hasMore based on returned records count
          state.hasMore = recipes.length === state.pageSize;
        }
      )
      .addCase(
        fetchRecipesBasedMealTypeReplaceRecipeModalThunk.rejected,
        (state, action) => {
          state.filterReplaceModalIsLoading = false;
          state.filterReplaceModalError = action.payload;
          state.filterReplaceModalFailedMessage = action.payload;
        }
      )

      // Fetch Meal Plan Details
      .addCase(fetchMealPlanDetails.pending, (state) => {
        state.mealPlanDetailsLoading = true;
        state.mealPlanDetailsError = null;
        state.mealPlanDetailsSuccessMessage = "";
        state.mealPlanDetailsFailedMessage = "";
      })
      .addCase(fetchMealPlanDetails.fulfilled, (state, action) => {
        state.mealPlanDetailsLoading = false;
        state.mealPlanDetails = action.payload;
        state.mealPlanDetailsSuccessMessage =
          action.payload?.message || "Meal plan details fetched successfully.";
      })
      .addCase(fetchMealPlanDetails.rejected, (state, action) => {
        state.mealPlanDetailsLoading = false;
        state.mealPlanDetailsError = action.payload;
        state.mealPlanDetailsFailedMessage =
          action.payload?.message || "Failed to fetch meal plan details.";
      })

      // Create Meal Plan
      .addCase(createMealPlanThunk.pending, (state) => {
        state.createMealPlanLoading = true;
        state.createMealPlanSuccessMessage = "";
        state.createMealPlanErrorMessage = "";
      })
      .addCase(createMealPlanThunk.fulfilled, (state, action) => {
        state.createMealPlanLoading = false;
        state.createMealPlanSuccessMessage =
          action.payload?.message || "Meal plan created successfully.";
      })
      .addCase(createMealPlanThunk.rejected, (state, action) => {
        state.createMealPlanLoading = false;
        state.createMealPlanErrorMessage =
          action.payload?.message || "Failed to create meal plan.";
      })

      // Macro Split Templates
      .addCase(fetchMacroSplitTemplatesThunk.pending, (state) => {
        state.macroSplitTemplatesLoading = true;
        state.macroSplitTemplatesError = null;
      })
      .addCase(fetchMacroSplitTemplatesThunk.fulfilled, (state, action) => {
        state.macroSplitTemplatesLoading = false;
        state.macroSplitTemplates = action.payload;
      })
      .addCase(fetchMacroSplitTemplatesThunk.rejected, (state, action) => {
        state.macroSplitTemplatesLoading = false;
        state.macroSplitTemplatesError = action.payload;
      });
  },
});

export const {
  resetFilteredRecipes,
  resetMealPlanDetails,
  resetMacroSplitTemplates,
  resetState,
  resetModalPagination,
  setModalPageNumber,
} = mealTagsSlice.actions;
export default mealTagsSlice.reducer;
